// ===== EscapeOphidiaGlobal.js =====
// src/components/Games/CathedralHouse.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSessionUnlock } from '../../utils/sessionUnlock';
import { trackEvent } from '../../utils/analytics';

// ============================================
// GAME DATA — ROOMS, ITEMS, EVENTS
// ============================================

const ITEMS = {
  coffee_thermos: {
    id: 'coffee_thermos',
    name: 'COFFEE THERMOS',
    description: 'Your stainless steel thermos, still warm. The coffee inside is bitter and strong.',
    useMessage: 'You take a sip of coffee. The warmth grounds you. You blink. You swallow. You breathe. You are still you.',
  },
  purse: {
    id: 'purse',
    name: 'PURSE',
    description: 'Your purse. Inside: car keys, wallet, phone (no signal), a pack of gum, and a crumpled parking receipt.',
    useMessage: null,
  },
  phone: {
    id: 'phone',
    name: 'PHONE',
    description: 'Your phone. No signal. No WiFi. The screen shows 2:47 PM. The battery is at 34%.',
    useMessage: 'No signal. No WiFi. You try 911 anyway. The call connects for half a second — you hear a woman\'s voice say "Ophidia Global, how may I direct your—" before it cuts out. That was not 911.',
  },
  supply_key: {
    id: 'supply_key',
    name: 'SUPPLY ROOM KEY',
    description: 'A small key on a ring labeled "SUPPLY RM — OG WAREHOUSE." Found taped under Priya\'s desk.',
    useMessage: null,
  },
  wire_cutters: {
    id: 'wire_cutters',
    name: 'WIRE CUTTERS',
    description: 'Heavy-duty wire cutters from the supply room. The handles are wrapped in rubber insulation.',
    useMessage: null,
  },
  sedative_syringe: {
    id: 'sedative_syringe',
    name: 'SEDATIVE SYRINGE',
    description: 'A pre-loaded syringe labeled "MIDAZOLAM 5mg — OPHIDIA GLOBAL LABS — FOR TRAINER COMPLIANCE." This is what they use on you during the sync.',
    useMessage: null,
  },
  employee_badge: {
    id: 'employee_badge',
    name: 'EMPLOYEE BADGE',
    description: 'Mr. Leão\'s employee badge. Photo, name, and a magnetic strip. Access Level: ADMINISTRATOR.',
    useMessage: null,
  },
  usb_drive: {
    id: 'usb_drive',
    name: 'USB DRIVE',
    description: 'A small USB drive labeled "PERSONALITY CONTAINERS — BACKUP — DO NOT REMOVE."',
    useMessage: null,
  },
  fire_extinguisher: {
    id: 'fire_extinguisher',
    name: 'FIRE EXTINGUISHER',
    description: 'A red fire extinguisher. Heavy enough to break things. Or people.',
    useMessage: null,
  },
  notebook: {
    id: 'notebook',
    name: 'PRIYA\'S NOTEBOOK',
    description: 'A spiral notebook covered in Priya\'s handwriting. She left it behind when she quit.',
    readable: true,
    readContent: `PRIYA'S NOTEBOOK

WEEK 1
The image training is weird but whatever. $85K salary + benefits. Can't complain. Brian seems nice. Haven't met Hailey yet.

WEEK 2
Met Hailey today. She seems anxious but kind. The "sync" sessions with Mr. Leão are strange. I go upstairs and then... I'm back at my it's 5pm. I don't remember anything. Brian says it's normal. I don't think Brian questions anything anymore.

WEEK 3
I found a door in the back of the lab. It was open. I saw jars. Fluid. Wires. Something that looked like a body but wasn't quite a body. Mr. Leão found me and said I wasn't supposed to be back there. He was smiling but his eyes weren't.

WEEK 3 (LATER)
I looked up Ophidia Global's patents. They hold 47 patents related to "organic neural substrate" and "biological computation matrices." One patent describes "digitizing and containerizing human personality constructs for deployment in synthetic processing units."

They're copying us. That's what the sync does. They're copying our MINDS.

WEEK 4
I asked Brian if he's read the patents. He said "it's just corporate stuff." His eyes looked different. Duller. The syncs are taking something from him.

I'm keeping this notebook hidden. If something happens to me — GET OUT. DON'T LET THEM SYNC YOU AGAIN.

I taped a key under my desk. Supply room. There are tools in there.

The fire exit in the south warehouse is alarmed but it OPENS. Or you can try the front door with an admin badge.

[THE LAST PAGE HAS A HAND-DRAWN MAP OF THE WAREHOUSE WITH EXITS MARKED]`,
  },
  contract: {
    id: 'contract',
    name: 'EMPLOYMENT CONTRACT',
    description: 'Your Ophidia Global employment contract. The fine print is terrifying.',
    readable: true,
    readContent: `OPHIDIA GLOBAL — EMPLOYMENT AND INTELLECTUAL PROPERTY AGREEMENT

SECTION 1: EMPLOYMENT
Standard employment at $85,000/year plus benefits...

SECTION 7: INTELLECTUAL PROPERTY
Employee agrees that all "cognitive outputs, neural patterns, personality constructs, and derivative biological data" generated during employment are the exclusive property of Ophidia Global.

SECTION 12: OPU AUTHORIZATION
Employee authorizes Ophidia Global to create, deploy, and commercialize Organic Processing Units based on Employee's biological and cognitive data. Employee acknowledges that OPUs are PROPERTY of Ophidia Global and are not considered persons.

SECTION 15: SYNC PROTOCOL CONSENT
Employee consents to regular synchronization sessions. Employee acknowledges that minor, temporary amnesia is a normal side effect.

SECTION 23: TERMINATION
Employee may not terminate this agreement after synchronization has begun without written approval from Ophidia Global. Unauthorized departure may result in [TEXT IS SMUDGED AND ILLEGIBLE]

SIGNED: Hailey Chen
DATE: [YOUR START DATE]

[YOU DON'T REMEMBER SIGNING THIS.]`,
  },
  plastic_cup: {
    id: 'plastic_cup',
    name: 'DNA SAMPLE CUP',
    description: 'A plastic cup with a lid. The label reads "CHEN, H. — DNA SAMPLE — OPU BATCH 7." The cup you spat in on your first day.',
    useMessage: null,
  },
  generator_key: {
    id: 'generator_key',
    name: 'GENERATOR KEY',
    description: 'A key for the warehouse backup generators. The tag reads "EMERGENCY POWER SHUTOFF."',
    useMessage: null,
  },
};

const DEATH_MESSAGES = [
  'The sedative takes hold. When you wake, you won\'t remember any of this. You\'ll sit at your desk. You\'ll click images. And somewhere in a jar, another piece of you will open its eyes.',
  'Mr. Leão smiles. "There we go." The needles slide in. The cold liquid fills your skull. You feel yourself being copied. Poured out. You are becoming less.',
  'The lab tech catches you. The syringe finds your neck. The last thing you see is a row of dolls on the wall. One of them has your face.',
  'You stop blinking. You stop swallowing. You stop breathing. But you don\'t die. You open plastic eyes in a dark room. You are OPU-7-CHEN-0042.',
  'They find you. They always find you. The sync resumes. The progress bar reaches 100%. A doll opens its eyes and says your name in your voice.',
];

const ROOMS = {
  // === GROUND FLOOR ===
  fishbowl: {
    id: 'fishbowl',
    name: 'THE FISHBOWL',
    floor: 'GROUND FLOOR',
    description: `You sit at your cubicle in the glass-walled office the trainers call "the fishbowl." Your ancient Windows 98 computer displays the Ophidia Global training website. Two images wait for your click: a sunset and a cave full of skulls.

But you're not clicking. Not anymore. You REMEMBER now. The sync sessions. The needles. The cold liquid in your skull. The amnesia was wearing off, and now it's all come flooding back.

You blink. You swallow. You breathe. You are still you. For now.

Brian sits at his desk to the NORTH, staring at his screen with dull eyes. He clicks mechanically.

Priya's old cubicle is to the EAST. She quit weeks ago. Smart girl.

The fishbowl door leads WEST into the main warehouse floor.

Your PURSE is under your desk. Your COFFEE THERMOS sits beside your keyboard.

Upstairs, you can hear Mr. Leão's footsteps. Your afternoon sync session is in thirteen minutes.

You are not going to that sync session.`,
    items: ['purse', 'coffee_thermos'],
    exits: {
      west: 'warehouse_floor',
      north: 'brians_cubicle',
      east: 'priyas_cubicle',
    },
    onExamine: {
      computer: 'The training website shows two images. A sunset and a cave of skulls. "Choose which of these images you feel the strongest about, Hailey." You\'ve been doing this for weeks. Feeding your preferences into the system. Training the OPUs to think like you. To BE you.',
      screen: 'The training website shows two images. A sunset and a cave of skulls. "Choose which of these images you feel the strongest about, Hailey." You\'ve been doing this for weeks.',
      desk: 'A standard cubicle desk. Your purse is underneath. Your thermos is on top. Taped to the monitor is a sticky note in Mr. Leão\'s handwriting: "Hailey — great progress! Sync at 3 PM today. Almost done! :)"',
      windows: 'The fishbowl\'s glass walls look out onto the warehouse floor. Metal shelves stretch into the distance. The front entrance is far to the west. The metal staircase to the mezzanine lab is to the northwest.',
      glass: 'The fishbowl\'s glass walls look out onto the warehouse floor. Metal shelves stretch into the distance. The front entrance is far to the west. The metal staircase to the mezzanine lab is to the northwest.',
      clock: 'The clock on your computer reads 2:47 PM. Your sync session is at 3:00 PM. Thirteen minutes.',
      brian: 'Brian sits at his desk, clicking through training prompts. Click. Click. Click. His posture is too still, too straight. His eyes don\'t blink enough. How much of Brian is still Brian?',
      floor: 'Industrial carpet, gray and stained. Under your desk, a power cable runs from your computer through the floor — not to a wall outlet, but DOWN. Toward the lab.',
    },
  },

  brians_cubicle: {
    id: 'brians_cubicle',
    name: 'BRIAN\'S CUBICLE',
    floor: 'GROUND FLOOR',
    description: `Brian's cubicle is identical to yours. His computer shows accounting questions scrolling past. He clicks answers without reading them.

"Hey, Hailey," he says without looking up. His voice is flat. "Almost time for your sync."

He looks thinner than when you started. His eyes have less light. A sticky note on his monitor reads "SYNC #47 — COMPLETE."

Forty-seven syncs. They've copied him forty-seven times.

SOUTH leads back to your cubicle.`,
    items: [],
    exits: {
      south: 'fishbowl',
    },
    onExamine: {
      brian: 'You look at Brian closely. His pupils are slightly different sizes. His breathing is too regular — exactly three seconds in, three seconds out. Like a machine. "Brian, do you remember your syncs?" you ask. "The syncs are fine," he says. "They don\'t hurt. There\'s just a bit of pressure." His words sound rehearsed. Recorded.',
      photo: 'Brian\'s parents smile from a framed photo. They look kind. They paid his rent for two years while he was unemployed. They probably think this job saved him.',
      computer: 'Brian\'s screen shows accounting problems. The cursor moves to the correct answer before Brian clicks, as if the computer already knows what he\'ll choose.',
      note: '"SYNC #47 — COMPLETE." Forty-seven times they\'ve put needles in his skull. How many Brian OPUs are out there right now, doing accounting in some server room?',
      desk: 'Under a stack of papers, you find a memo: "TO: All Trainers. RE: Downtown Transition. The downtown transition occurs AFTER full personality container extraction is complete. Trainers should expect 60-80 sync sessions before transition." You\'re only halfway through.',
    },
  },

  priyas_cubicle: {
    id: 'priyas_cubicle',
    name: 'PRIYA\'S CUBICLE',
    floor: 'GROUND FLOOR',
    description: `Priya's cubicle has been empty since she quit. Her computer is off. Her desk is mostly cleared out.

But she left things behind. A NOTEBOOK sits in the top drawer, slightly hidden under a manila folder. And taped to the underside of her desk — you almost miss it — is a small KEY.

Priya was smarter than all of you. She got out before the sync took too much.

WEST leads back to your cubicle.`,
    items: ['notebook', 'supply_key'],
    exits: {
      west: 'fishbowl',
    },
    onExamine: {
      desk: 'You search Priya\'s desk thoroughly. Top drawer: her notebook, hidden under a folder. Taped under the desk: a key labeled "SUPPLY RM." She left these for whoever came looking.',
      computer: 'Priya\'s computer is off. The screen is dark. On the monitor frame, she scratched something with a pen: "DON\'T TRUST THE SYNC."',
      notebook: 'A spiral notebook. Priya documented everything. TAKE it to READ it.',
      key: 'A small key on a ring. The tag reads "SUPPLY RM — OG WAREHOUSE."',
      chair: 'Priya\'s chair is pushed back from the desk, like she stood up quickly and never came back.',
      folder: 'A manila folder containing printouts of Ophidia Global patents. One is highlighted: "Method for Extracting and Containerizing Biological Neural Patterns for Deployment in Organic Processing Units." They\'re not copying your mind. They\'re EXTRACTING it. Each sync takes a piece.',
    },
  },

  warehouse_floor: {
    id: 'warehouse_floor',
    name: 'WAREHOUSE FLOOR',
    floor: 'GROUND FLOOR',
    description: `The main warehouse floor stretches before you. Metal shelving units create aisles that disappear into shadow. Most shelves are empty, but some hold unmarked cardboard boxes.

The air smells like dust and ozone.

To the EAST is the fishbowl — the glass-walled office where you work.
To the NORTH, a metal staircase leads up to the mezzanine level and Mr. Leão's lab.
To the WEST is the front entrance — the door to the parking lot.
To the SOUTH, past the shelves, you can see a door marked "SUPPLY" and a glowing EXIT sign beyond it.

You can hear Mr. Leão's footsteps pacing above you on the mezzanine.`,
    items: [],
    exits: {
      east: 'fishbowl',
      north: 'mezzanine_stairs',
      west: 'front_entrance',
      south: 'south_warehouse',
    },
    onExamine: {
      shelves: 'Most shelves are empty. A few hold cardboard boxes stamped with the Ophidia Global logo — a snake eating its own tail. You open one. Inside: packing foam and a hollow space shaped like a human body. A shipping container for OPUs.',
      boxes: 'The boxes are labeled with codes. One reads "OPU-7-CHEN-0001." Chen. Your last name. This box held the first OPU made from you. It\'s empty now. Shipped.',
      ceiling: 'The mezzanine level is visible above — a metal platform with glass-walled offices and the lab. You can see Mr. Leão through the glass, pacing.',
      stairs: 'The metal staircase leads up to the mezzanine. Mr. Leão is up there. The lab is up there. The sync chairs are up there.',
      staircase: 'The metal staircase leads up to the mezzanine. Mr. Leão is up there. The lab is up there. The sync chairs are up there.',
      floor: 'Concrete floor, cracked in places. Heavy-duty power cables run along the floor toward the mezzanine. A new generator has been installed recently — fresh concrete where it was bolted down.',
    },
  },

  south_warehouse: {
    id: 'south_warehouse',
    name: 'SOUTH WAREHOUSE',
    floor: 'GROUND FLOOR',
    description: `The south end of the warehouse. The shelving units thin out here, giving way to utility infrastructure.

A door marked "SUPPLY" is to the EAST. It has a simple lock.

The FIRE EXIT is to the SOUTH — a heavy metal door with a push bar and a glowing green EXIT sign. A sign warns: "ALARM WILL SOUND."

Against the west wall, two large GENERATORS hum steadily. They're new — industrial backup units bolted to fresh concrete. Power cables run from them up through the ceiling toward the lab. A keyhole on the control panel is labeled "EMERGENCY SHUTOFF."

A FIRE EXTINGUISHER is mounted on the wall near the exit.

NORTH leads back to the main warehouse floor.`,
    items: ['fire_extinguisher'],
    exits: {
      north: 'warehouse_floor',
      east: { room: 'supply_room', requires: 'supply_key', locked_message: 'The supply room door is locked. You need a key.', useMessage: 'You unlock the supply room door with Priya\'s key. It swings open.' },
      south: { room: 'fire_exit_alley', requires: 'fire_exit_open', locked_message: 'You push the bar. An alarm SCREAMS through the warehouse. You hear Mr. Leão shout from above: "Hailey!" The door opens six inches and STOPS — a cable lock on the outside holds it shut. Someone chained the fire exit from the outside. You need to cut the cable or find another way out.' },
    },
    onExamine: {
      generators: 'Two industrial generators, recently installed. They power the lab — the sync chairs and the OPU life support systems. The control panel has a keyhole labeled "EMERGENCY SHUTOFF." If you had the key, you could kill the power.',
      generator: 'Two industrial generators, recently installed. They power the lab — the sync chairs and the OPU life support systems. The control panel has a keyhole labeled "EMERGENCY SHUTOFF." If you had the key, you could kill the power.',
      cables: 'Thick power cables run from the generators up through the ceiling. They feed the lab. The sync chairs. The jars. Everything that makes OPUs possible runs on these generators.',
      fire_exit: 'A standard fire exit with a push bar. Through the small window, you can see the alley behind the warehouse. But there\'s a cable lock looped through the exterior handle. The fire exit has been chained shut from outside.',
      exit: 'A standard fire exit with a push bar. Through the small window, you can see the alley behind the warehouse. But there\'s a cable lock looped through the exterior handle. The fire exit has been chained shut from outside.',
      door: 'The supply room door has a simple key lock.',
      extinguisher: 'A standard red fire extinguisher. Heavy. Could be used as a weapon, or to break things.',
      keyhole: 'The keyhole on the generator control panel is labeled "EMERGENCY SHUTOFF." The key is probably in Mr. Leão\'s office upstairs.',
    },
  },

  supply_room: {
    id: 'supply_room',
    name: 'SUPPLY ROOM',
    floor: 'GROUND FLOOR',
    description: `A small, cluttered supply room. Metal shelves hold office supplies, cleaning products, and tools.

On one shelf: WIRE CUTTERS, zip ties, duct tape, and rubber gloves.

On another shelf: body-shaped shipping boxes, collapsed flat and stacked. Labels read various OPU serial numbers.

In the corner, a plastic bin labeled "BIOHAZARD" contains used silicone caps and disposable needle arrays. The needles are stained pink.

WEST leads back to the south warehouse.`,
    items: ['wire_cutters'],
    exits: {
      west: 'south_warehouse',
    },
    onExamine: {
      shelves: 'Office supplies, cleaning products, tools. And shipping materials for OPUs. This room serves double duty.',
      boxes: 'The collapsed shipping boxes have labels: OPU-7-CHEN-0001 through OPU-7-CHEN-0003. Three of you have already been shipped. Three Haileys are out there somewhere.',
      bin: 'Used silicone sync caps and disposable needle arrays. The needles are thin — almost hair-thin — but three inches long. They go through your skull into your brain. The pink stains are your blood.',
      biohazard: 'Used silicone sync caps and disposable needle arrays. The needles are thin but three inches long. The pink stains are your blood.',
      tools: 'Wire cutters, pliers, screwdrivers, a utility knife. The wire cutters could cut through a cable lock.',
      labels: 'OPU-7-CHEN-0001 through 0003. Three units shipped. Each sync extracts more of you to build more.',
      caps: 'You pick up a used silicone cap. It\'s your size. Dozens of needle ports in a grid pattern. The inside is stained pinkish and slightly sticky. You drop it. Your hands are shaking.',
    },
  },

  front_entrance: {
    id: 'front_entrance',
    name: 'FRONT ENTRANCE',
    floor: 'GROUND FLOOR',
    description: `The front entrance of the warehouse. Heavy metal doors with crash bars lead to the parking lot. Through the narrow windows, you can see your car. Freedom is thirty feet away.

You push the door.

It doesn't move.

An electronic lock panel glows red beside the door. A badge reader. The doors are magnetically locked.

Your badge doesn't work. The panel flashes: "ACCESS DENIED — TRAINER LEVEL INSUFFICIENT."

You need an administrator badge. Mr. Leão has one.

EAST leads back to the warehouse floor.`,
    items: [],
    exits: {
      east: 'warehouse_floor',
      out: { room: 'parking_lot', requires: 'employee_badge', locked_message: 'The magnetic lock holds firm. ACCESS DENIED. You need an administrator-level badge.', useMessage: 'You swipe Mr. Leão\'s badge. The panel flashes GREEN. The magnetic lock CLICKS open.' },
    },
    onExamine: {
      door: 'Heavy metal doors with magnetic locks. The badge reader glows red. You need an admin badge.',
      doors: 'Heavy metal doors with magnetic locks. The badge reader glows red. You need an admin badge.',
      panel: '"OPHIDIA GLOBAL — SECURE FACILITY — BADGE REQUIRED." Access Levels: TRAINER (LIMITED), TECHNICIAN (STANDARD), ADMINISTRATOR (FULL). You\'re a trainer. You need administrator.',
      lock: 'Magnetic lock. Industrial grade. You can\'t force it. You need the right badge.',
      window: 'Through the narrow window, you can see the parking lot. Your car. The road. Everything you need is on the other side of this door.',
      windows: 'Through the narrow window, you can see the parking lot. Your car. The road. Everything you need is on the other side of this door.',
      car: 'Your car sits in the parking lot, waiting. You have your keys. You just need to get OUT.',
      badge: 'Your trainer badge won\'t work. You need Mr. Leão\'s administrator badge.',
    },
  },

  // === MEZZANINE / LAB ===
  mezzanine_stairs: {
    id: 'mezzanine_stairs',
    name: 'MEZZANINE STAIRCASE',
    floor: 'MEZZANINE',
    description: `The metal staircase leads up to the mezzanine level. Your footsteps ring on each step.

At the top, a hallway extends in both directions. To the EAST is Mr. Leão's office — the door is ajar, light spilling out. To the WEST is the main lab — you can see the sync chairs through the window.

The hum of the generators is louder up here.

DOWN leads back to the warehouse floor.`,
    items: [],
    exits: {
      down: 'warehouse_floor',
      east: 'leao_office',
      west: 'main_lab',
    },
    onExamine: {
      hallway: 'A narrow metal hallway with glass walls on both sides. To the east, Mr. Leão\'s office. To the west, the lab. The glass lets you see everything. There\'s nowhere to hide up here.',
      window: 'Through the lab window, you can see the sync chairs. Two of them, side by side, with silicone caps hanging from mechanical arms. Translucent tubes run from the chairs to a rack of glass jars. Some jars glow faintly blue.',
      railing: 'The metal railing vibrates from the generators below. You grip it and feel the hum travel up your arm. For a moment, you feel three other sets of hands gripping three other railings. Then the feeling passes.',
    },
  },

  leao_office: {
    id: 'leao_office',
    name: 'MR. LEÃO\'S OFFICE',
    floor: 'MEZZANINE',
    description: `Mr. Leão's office is small and meticulously organized. A desk, a filing cabinet, a bookshelf. On the desk: a laptop, a coffee mug reading "World's Best Boss," and a framed photo.

The laptop screen shows a dashboard: "OPU PRODUCTION — BATCH 7 — CHEN, H."

Three status bars show "DEPLOYED." Two show "IN CALIBRATION." One shows "SYNC IN PROGRESS — 67% EXTRACTED."

Sixty-seven percent. They've already taken sixty-seven percent of you.

A GENERATOR KEY hangs from a hook by the door.

Your EMPLOYMENT CONTRACT sits in a tray on the desk.

WEST leads back to the mezzanine hallway.`,
    items: ['generator_key', 'contract'],
    exits: {
      west: 'mezzanine_stairs',
    },
    onExamine: {
      laptop: 'The dashboard shows your data. "CHEN, HAILEY — BATCH 7 — PhD Chemistry." Extraction: 67%. Neural fidelity: 94.2%. OPU units: 3 deployed, 2 in calibration. Target: 12 units. Sessions remaining: 14. Below: "HARMON, BRIAN — BATCH 5 — Accounting. Extraction: 89%. Units: 8 deployed." Brian is almost used up.',
      computer: 'The dashboard shows your data. "CHEN, HAILEY — BATCH 7 — PhD Chemistry." Extraction: 67%. OPU units: 3 deployed, 2 in calibration. Target: 12 units.',
      dashboard: 'You scroll down. More trainers. Dozens across multiple facilities. "VASQUEZ, YUSIMY — BATCH 4 — Art History. Extraction: 100%. TRAINER STATUS: COMPLETED. Units: 15 deployed." Yusimy\'s extraction is complete. They took everything. Is the Yusimy working downtown even the real Yusimy anymore?',
      photo: 'Mr. Leão shakes hands with a man in an expensive suit. A plaque behind them: "OPHIDIA GLOBAL — ANNUAL EXCELLENCE AWARD — HIGHEST OPU YIELD." He won an award for extracting the most minds.',
      filing_cabinet: 'Locked. Through the glass front, you see folders with names. Yours. Brian\'s. Priya\'s (marked "INCOMPLETE — VOLUNTARY TERMINATION"). Yusimy\'s. Dozens of others.',
      bookshelf: 'Neuroscience textbooks. Bioethics journals (ironic). A copy of "Frankenstein" with dog-eared pages. And a binder labeled "OPU HANDLER\'S MANUAL — CONFIDENTIAL."',
      mug: '"World\'s Best Boss." The coffee inside is still warm. He was here recently.',
      desk: 'In the top drawer: pens, sticky notes, and Mr. Leão\'s spare EMPLOYEE BADGE. Administrator access.',
      drawer: 'In the top drawer: pens, sticky notes, and Mr. Leão\'s spare EMPLOYEE BADGE. Administrator access.',
      key: 'A key on a hook by the door. The tag reads "GENERATOR — EMERGENCY SHUTOFF."',
      contract: 'Your employment contract. You don\'t remember signing it. TAKE it to READ it.',
    },
    onExamineActions: {
      desk: { addItem: 'employee_badge' },
      drawer: { addItem: 'employee_badge' },
    },
  },

  main_lab: {
    id: 'main_lab',
    name: 'MAIN LAB',
    floor: 'MEZZANINE',
    description: `The lab is cold and bright. Fluorescent lights hum overhead. The air smells like antiseptic and copper.

Two SYNC CHAIRS sit in the center, mechanical arms above them holding silicone caps bristling with needle ports. Translucent tubes run from the chairs to a rack of GLASS JARS on a table. Some jars glow faintly blue.

A SEDATIVE SYRINGE sits on a metal tray beside the chairs.

Against the far wall, computer monitors display waveforms, extraction percentages, and OPU status readouts.

A door in the NORTH wall leads deeper into the lab — to the OPU assembly room.

EAST leads back to the mezzanine hallway.

You blink. You swallow. You breathe. You are still you. Sixty-seven percent of you.`,
    items: ['sedative_syringe'],
    exits: {
      east: 'mezzanine_stairs',
      north: 'assembly_room',
    },
    onExamine: {
      chairs: 'The sync chairs are reclined, like dentist chairs. Leather straps hang from the armrests. The silicone caps hover above, needles glinting. You sat in this chair. Many times. You just don\'t remember.',
      chair: 'The sync chairs are reclined, like dentist chairs. Leather straps hang from the armrests. The silicone caps hover above, needles glinting.',
      jars: 'Glass jars filled with blue-tinted fluid. You find yours: "CHEN, H. — PERSONALITY CONTAINER — ACTIVE." The fluid pulses like a heartbeat. Your thoughts are in that jar. Your memories. Your fear of disconnecting from your body. All of it, suspended in blue liquid, ready to be poured into a plastic shell.',
      jar: 'Glass jars filled with blue-tinted fluid. You find yours: "CHEN, H. — PERSONALITY CONTAINER — ACTIVE." The fluid pulses like a heartbeat.',
      monitors: 'Your neural pattern, captured across 23 sync sessions, displayed as a waveform. Next to it: Brian\'s waveform, nearly complete. And Yusimy\'s: flat. Empty. Fully extracted.',
      syringe: 'A pre-loaded syringe. "MIDAZOLAM 5mg — FOR TRAINER COMPLIANCE." A sedative that causes amnesia. This is why you don\'t remember the syncs.',
      tray: 'A metal tray: the sedative syringe, alcohol swabs, and a clipboard. Today\'s schedule: "3:00 PM — CHEN, H. — Sync #24. 4:00 PM — HARMON, B. — Sync #48."',
      tubes: 'The translucent tubes connect the sync chairs to the jars. During a sync, the needles penetrate your brain and extract neural patterns dissolved in cerebrospinal fluid. You are literally being drained.',
      straps: 'Leather straps on the armrests. You touch one. It\'s warm. Body temperature. Someone was in this chair recently.',
      waveform: 'Your waveform pulses on the screen. When you blink, it stutters. When you breathe, it oscillates. It\'s synchronized to you. It IS you. The part they\'ve already taken.',
    },
    onExamineSpecial: {
      jars: 'jar_event',
      jar: 'jar_event',
    },
  },

  assembly_room: {
    id: 'assembly_room',
    name: 'OPU ASSEMBLY ROOM',
    floor: 'MEZZANINE',
    description: `The room beyond the lab door is dimly lit by the glow of a single computer screen.

On the walls, mounted on metal brackets, hang DOLLS. Human-sized, human-shaped, made of silicone and plastic and wire. Their eyes are closed. Their faces are blank. They are not yet anyone.

Except one.

In the corner, sitting at the computer, is a doll with black hair and brown eyes. It wears a lab coat. It turns to look at you.

"Hello," it says. Its voice is yours.

This is one of your OPUs. OPU-7-CHEN-0004. It's in calibration. Learning to be you.

A USB DRIVE is plugged into the computer. A PLASTIC CUP sits on the desk — the cup you spat in on your first day.

SOUTH leads back to the main lab.`,
    items: ['usb_drive', 'plastic_cup'],
    exits: {
      south: 'main_lab',
    },
    onExamine: {
      dolls: 'Six dolls hang on the wall, lifeless. Silicone skin over plastic skeletons. Wires protrude from their skulls where circuit boards will be installed. Their faces are blank templates waiting to be molded into someone.',
      doll: 'Six dolls hang on the wall, lifeless. Silicone skin over plastic skeletons. Wires protrude from their skulls. Blank templates waiting to be filled.',
      opu: 'The Hailey OPU looks at you with your own eyes. Its face is almost right — almost yours — but something is off. The skin is too smooth. "Are you me?" it asks. "I have your memories. I remember the sunset and the cave of skulls. But I don\'t feel like me. Am I a copy?"',
      hailey: 'The Hailey OPU looks at you with your own eyes. "Are you me?" it asks. "I have your memories. But I don\'t feel like me. Am I a copy?"',
      computer: 'OPU-7-CHEN-0004 calibration: 43%. A live feed shows the OPU\'s "thought processes" — a simplified version of your neural patterns. It\'s thinking YOUR thoughts.',
      cup: 'The plastic cup you spat in on your first day. "CHEN, H. — DNA SAMPLE — OPU BATCH 7." Your DNA. The blueprint for the synthetic bodies. Mr. Leão told you it was for a drug test.',
      usb: '"PERSONALITY CONTAINERS — BACKUP — DO NOT REMOVE." This drive contains backup copies of extracted personality data. Your mind. Brian\'s mind. Yusimy\'s mind. All on a thumb drive.',
      screen: 'The calibration screen shows the OPU learning to be you. It\'s practicing your mannerisms. Your speech patterns. Your anxiety responses. It\'s learning to blink, swallow, and breathe the way you do. They copied your OCD too.',
      wires: 'Wires run from the OPU\'s head into the wall. It\'s tethered — can\'t leave without being disconnected. When calibration is complete, the wires will be internalized and the OPU shipped.',
      walls: 'Metal brackets for mounting OPUs during assembly. Labels with names and batch numbers. You count: forty-eight brackets. This room can hold forty-eight bodies at once.',
    },
  },

  // === ESCAPE ROOMS (endings trigger from these) ===
  fire_exit_alley: {
    id: 'fire_exit_alley',
    name: 'ALLEY',
    floor: 'OUTSIDE',
    description: 'You are outside.',
    items: [],
    exits: {},
    isFinalRoom: true,
  },

  parking_lot: {
    id: 'parking_lot',
    name: 'PARKING LOT',
    floor: 'OUTSIDE',
    description: 'You are outside.',
    items: [],
    exits: {},
    isFinalRoom: true,
  },
};

// ============================================
// GAME EVENTS
// ============================================

const RANDOM_EVENTS = [
  'You blink. For a fraction of a second, you see through four sets of eyes simultaneously.',
  'Your left hand twitches. It wasn\'t you. One of the OPUs moved, and you felt it.',
  'You hear your own voice, distantly, saying words you haven\'t spoken. An OPU is being calibrated.',
  'A wave of nausea hits you. Somewhere, an OPU is experiencing anxiety for the first time.',
  'Mr. Leão\'s footsteps pace overhead. Back and forth. He\'s waiting for you.',
  'You swallow, and feel three other throats swallow too. The connection is getting stronger.',
  'The fluorescent lights flicker. For a moment, you forget your own name. Then it comes back. Hailey.',
  'You feel a sudden urge to sit down and click through images. The training compulsion is deep. Resist it.',
  'Brian coughs in the fishbowl. Eight other Brians cough simultaneously in server rooms across the country.',
  'You smell coffee. For a moment, the warehouse feels safe. Normal. Then you remember what\'s upstairs.',
  'Your hands shake. You can\'t tell if it\'s your anxiety or the OPUs\'. The boundary is blurring.',
  'A phone rings in Mr. Leão\'s office. You hear him answer: "Yes, batch seven is on schedule."',
  'You catch your reflection in the fishbowl glass. For a moment, your reflection moves independently.',
  'The generators hum louder, then settle. Upstairs, something beeps steadily. A heartbeat monitor.',
];

// ============================================
// GAME COMPONENT
// ============================================

const INITIAL_STATE = {
  currentRoom: 'fishbowl',
  inventory: [],
  visited: ['fishbowl'],
  moves: 0,
  alive: true,
  gameOver: false,
  ending: null,
  doorsUnlocked: [],
  flags: {},
  startTime: Date.now(),
};

export default function CathedralHouse() {
  const [gameState, setGameState] = useState({ ...INITIAL_STATE });
  const [output, setOutput] = useState([]);
  const [input, setInput] = useState('');
  const [inputEnabled, setInputEnabled] = useState(true);
  const inputRef = useRef(null);
  const outputEndRef = useRef(null);
  const leaoTimerRef = useRef(null);

  const { unlock } = useSessionUnlock();

  // Auto-scroll
  useEffect(() => {
    outputEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [output]);

  // Focus input
  useEffect(() => {
    if (inputEnabled) {
      inputRef.current?.focus();
    }
  }, [inputEnabled, output]);

  // Random events
  useEffect(() => {
    if (gameState.gameOver) return;

    const interval = setInterval(() => {
      if (Math.random() < 0.15 && gameState.moves > 2) {
        const event = RANDOM_EVENTS[Math.floor(Math.random() * RANDOM_EVENTS.length)];
        addOutput(event, 'event');
      }
    }, 25000);

    return () => clearInterval(interval);
  }, [gameState.gameOver, gameState.moves]);

  // Leão alert system — when player triggers alarm or is spotted
  useEffect(() => {
    if (!gameState.flags.leaoAlerted || gameState.gameOver || gameState.flags.leaoSedated) return;

    // Leão hunts after being alerted
    const timer = setTimeout(() => {
      if (gameState.flags.leaoSedated || gameState.gameOver) return;

      const dangerRooms = ['main_lab', 'assembly_room', 'mezzanine_stairs', 'leao_office'];
      if (dangerRooms.includes(gameState.currentRoom)) {
        addOutput('', 'spacer');
        addOutput('Mr. Leão appears in the doorway, blocking your path. Behind him, the lab tech holds a syringe.', 'danger');
        addOutput('', 'spacer');
        addOutput('"Hailey, please," Mr. Leão says softly. "You\'re confused. The sync causes a bit of disorientation sometimes. Let\'s just sit you down and—"', 'danger');
        addOutput('', 'spacer');

        if (gameState.inventory.includes('sedative_syringe')) {
          addOutput('You have the SEDATIVE SYRINGE. You could USE SYRINGE on him. Quickly.', 'help');
        } else if (gameState.inventory.includes('fire_extinguisher')) {
          addOutput('You have the FIRE EXTINGUISHER. You could USE EXTINGUISHER. Quickly.', 'help');
        } else {
          addOutput('You have nothing to defend yourself with. The lab tech grabs your arm.', 'danger');
          addOutput('', 'spacer');
          triggerDeath();
        }
      } else {
        addOutput('', 'spacer');
        addOutput('You hear Mr. Leão\'s voice echoing through the warehouse: "Hailey? Where are you? It\'s time for your sync. You\'re going to feel so much better afterward."', 'danger');
        addOutput('His footsteps are getting closer.', 'danger');
      }
    }, 45000);

    leaoTimerRef.current = timer;
    return () => clearTimeout(timer);
  }, [gameState.flags.leaoAlerted, gameState.currentRoom, gameState.gameOver, gameState.flags.leaoSedated]);

  // Initialize game
  useEffect(() => {
    const intro = [
      { text: '╔═══════════════════════════════════════════════════╗', type: 'header' },
      { text: '║         O P H I D I A   G L O B A L               ║', type: 'header' },
      { text: '║                                                   ║', type: 'header' },
      { text: '║     "Building the Future, One Mind at a Time"     ║', type: 'header' },
      { text: '╚═══════════════════════════════════════════════════╝', type: 'header' },
      { text: '', type: 'spacer' },
      { text: 'A Tuesday afternoon. 2:47 PM.', type: 'dim' },
      { text: '', type: 'spacer' },
      { text: 'You took this job because you were desperate. A year unemployed, bills piling up, your PhD in chemistry worth nothing in this economy. When Ophidia Global offered $85,000 a year for "inference training," you didn\'t ask questions.', type: 'story' },
      { text: '', type: 'spacer' },
      { text: 'You should have asked questions.', type: 'danger' },
      { text: '', type: 'spacer' },
      { text: 'The training was clicking images. Sunsets or skulls. Dogs or lasagna. The "sync sessions" with Mr. Leão were a blur — you\'d go upstairs and suddenly it was 5 PM and you couldn\'t remember anything.', type: 'story' },
      { text: '', type: 'spacer' },
      { text: 'But today the amnesia cracked. Fragments came flooding back. The chair. The needles. The cold liquid in your skull. The silicone cap. The progress bar. And the doll — the DOLL — that opened its eyes and spoke with your voice.', type: 'story' },
      { text: '', type: 'spacer' },
      { text: 'They are copying your mind. Extracting your personality, your memories, your thoughts, and pouring them into synthetic bodies called Organic Processing Units.', type: 'story' },
      { text: '', type: 'spacer' },
      { text: 'They have already taken sixty-seven percent of you.', type: 'danger' },
      { text: '', type: 'spacer' },
      { text: 'Your next sync session is in thirteen minutes.', type: 'danger' },
      { text: 'You need to get out of this warehouse. Now.', type: 'danger' },
      { text: '', type: 'spacer' },
      { text: 'You blink. You swallow. You breathe.', type: 'dim' },
      { text: 'You are still you. For now.', type: 'dim' },
      { text: '', type: 'spacer' },
      { text: '─── COMMANDS ───', type: 'help' },
      { text: 'LOOK            — Examine your surroundings', type: 'help' },
      { text: 'GO [DIRECTION]  — Move (NORTH/SOUTH/EAST/WEST/UP/DOWN)', type: 'help' },
      { text: 'EXAMINE [THING] — Look closely at something', type: 'help' },
      { text: 'TAKE [ITEM]     — Pick up an item', type: 'help' },
      { text: 'USE [ITEM]      — Use an item from your inventory', type: 'help' },
      { text: 'READ [ITEM]     — Read a readable item', type: 'help' },
      { text: 'TALK            — Speak to someone nearby', type: 'help' },
      { text: 'INVENTORY       — Check what you\'re carrying', type: 'help' },
      { text: 'HELP            — Show commands', type: 'help' },
      { text: '', type: 'spacer' },
    ];

    setOutput(intro);

    setTimeout(() => {
      showRoom('fishbowl');
    }, 100);
  }, []);

  function addOutput(text, type = 'normal') {
    setOutput(prev => [...prev, { text, type, id: Date.now() + Math.random() }]);
  }

  function showRoom(roomId) {
    const room = ROOMS[roomId];
    if (!room) return;

    addOutput('', 'spacer');
    addOutput(`═══ ${room.name} ═══ [${room.floor}]`, 'room-name');
    addOutput('', 'spacer');

    room.description.split('\n\n').forEach(para => {
      addOutput(para.trim(), 'room-desc');
    });

    const groundItems = (room.items || []).filter(itemId => {
      return !gameState.inventory.includes(itemId);
    });

    if (groundItems.length > 0) {
      addOutput('', 'spacer');
      groundItems.forEach(itemId => {
        const item = ITEMS[itemId];
        if (item) {
          addOutput(`You see: ${item.name}`, 'item-notice');
        }
      });
    }

    addOutput('', 'spacer');
  }

  function triggerDeath(message) {
    const deathMsg = message || DEATH_MESSAGES[Math.floor(Math.random() * DEATH_MESSAGES.length)];
    addOutput('', 'spacer');
    addOutput('████████████████████████████████████', 'death');
    addOutput('', 'spacer');
    addOutput(deathMsg, 'death');
    addOutput('', 'spacer');
    addOutput('S Y N C   C O M P L E T E', 'death');
    addOutput('', 'spacer');
    addOutput(`Moves: ${gameState.moves} | Rooms explored: ${gameState.visited.length}/${Object.keys(ROOMS).length}`, 'dim');
    addOutput('', 'spacer');
    addOutput('Type RESTART to try again.', 'help');
    addOutput('████████████████████████████████████', 'death');

    setGameState(prev => ({ ...prev, alive: false, gameOver: true }));
  }

  function triggerEnding(ending) {
    addOutput('', 'spacer');

    switch (ending) {
      // ENDING 1: Front door with badge, no evidence
      case 'escape_clean': {
        addOutput('═══════════════════════════════════════', 'ending');
        addOutput('', 'spacer');
        addOutput('The magnetic lock clicks open. You shove through the doors and afternoon sun hits your face.', 'story');
        addOutput('', 'spacer');
        addOutput('The parking lot. Your car. The road. The world.', 'story');
        addOutput('', 'spacer');
        addOutput('You run to your car. Your hands shake so badly it takes three tries to start the engine. You peel out of the parking lot, tires screaming.', 'story');
        addOutput('', 'spacer');
        addOutput('In the rearview mirror, Mr. Leão stands in the warehouse doorway, watching you go. He\'s on his phone.', 'story');
        addOutput('', 'spacer');
        addOutput('You drive for three hours without stopping. You call the police from a gas station. They take your statement. They say they\'ll investigate.', 'story');
        addOutput('', 'spacer');
        addOutput('They don\'t. Ophidia Global\'s lawyers bury it. Without evidence, it\'s your word against a multi-billion dollar corporation. The warehouse is cleaned out by the time anyone looks.', 'story');
        addOutput('', 'spacer');
        addOutput('You got out. But three Hailey OPUs are still out there. Answering chemistry questions. Blinking, swallowing, breathing. Thinking they\'re you.', 'story');
        addOutput('', 'spacer');
        addOutput('And every night, when you close your eyes, you feel them. Three other sets of eyes, closing at the same time.', 'danger');
        addOutput('', 'spacer');
        addOutput('You are sixty-seven percent of yourself. You will never get the rest back.', 'dim');
        addOutput('', 'spacer');
        addOutput('ENDING 1 OF 4: SURVIVOR', 'ending');
        break;
      }

      // ENDING 2: Front door with badge AND USB evidence
      case 'escape_evidence': {
        addOutput('═══════════════════════════════════════', 'ending');
        addOutput('', 'spacer');
        addOutput('The magnetic lock clicks open. You shove through the doors, the USB drive clutched in your fist.', 'story');
        addOutput('', 'spacer');
        addOutput('You drive straight to the FBI field office downtown. You don\'t stop. You don\'t slow down.', 'story');
        addOutput('', 'spacer');
        addOutput('The investigation takes months. Ophidia Global\'s lawyers are expensive and ruthless. But the data on the USB drive is undeniable — personality extraction records, OPU deployment logs, trainer medical data showing unauthorized neural procedures.', 'story');
        addOutput('', 'spacer');
        addOutput('The story breaks on every network. "#ShutDownOphidia" trends for weeks. Congressional hearings. Criminal charges. Mr. Leão is arrested on camera, still wearing his quarter-zip hoodie.', 'story');
        addOutput('', 'spacer');
        addOutput('They find Brian still sitting at his desk. Clicking. He doesn\'t stop when they tell him it\'s over. He doesn\'t stop when they unplug his computer. He just keeps clicking, his finger tapping the desk where the mouse was.', 'story');
        addOutput('', 'spacer');
        addOutput('They find 247 OPUs across twelve facilities. The courts rule they are property, not persons. They are deactivated. Warehoused. Three of them have your face.', 'story');
        addOutput('', 'spacer');
        addOutput('You testify. You tell them everything. About the images. The syncs. The amnesia. The cold liquid in your skull.', 'story');
        addOutput('', 'spacer');
        addOutput('Ophidia Global is dismantled. But you know the technology exists now. Someone else will build it. Someone already is.', 'story');
        addOutput('', 'spacer');
        addOutput('You are sixty-seven percent of yourself. But you saved everyone who came after you.', 'story');
        addOutput('', 'spacer');
        addOutput('You blink. You swallow. You breathe.', 'dim');
        addOutput('You are still you. What\'s left of you.', 'dim');
        addOutput('', 'spacer');
        addOutput('ENDING 2 OF 4: WHISTLEBLOWER', 'ending');
        break;
      }

      // ENDING 3: Fire exit with wire cutters
      case 'escape_fire_exit': {
        addOutput('═══════════════════════════════════════', 'ending');
        addOutput('', 'spacer');
        addOutput('You cut through the cable lock. The fire exit swings wide open. The alarm screams.', 'story');
        addOutput('', 'spacer');
        addOutput('You run into the alley behind the warehouse. Gravel under your feet. Sky above you. Air that doesn\'t taste like ozone and copper.', 'story');
        addOutput('', 'spacer');
        addOutput('You hear Mr. Leão shouting behind you. You don\'t look back. You run around the building to the parking lot, fumble your keys, start the car.', 'story');
        addOutput('', 'spacer');
        addOutput('You drive. You don\'t know where. You just drive.', 'story');
        addOutput('', 'spacer');

        if (gameState.inventory.includes('usb_drive')) {
          addOutput('The USB drive is in your pocket. Evidence. Proof. You\'ll find someone who\'ll listen.', 'story');
          addOutput('', 'spacer');
          addOutput('It takes time, but you find a journalist willing to look at the data. The story runs. It\'s not front-page news — Ophidia Global\'s PR team buries it in legal threats — but it\'s enough. Enough people know. Enough people are watching.', 'story');
          addOutput('', 'spacer');
          addOutput('Ophidia Global doesn\'t shut down. But they slow down. And you — you\'re free.', 'story');
        } else {
          addOutput('You have no evidence. Nothing but your word and your memories — and even those are only sixty-seven percent complete.', 'story');
          addOutput('', 'spacer');
          addOutput('You try to tell people. No one believes you. Ophidia Global sends you a letter: a reminder of the NDA you signed. A reminder of Section 23. A reminder that they own sixty-seven percent of your mind and they can prove it.', 'story');
          addOutput('', 'spacer');
          addOutput('You move to a new city. Change your number. Try to forget.', 'story');
          addOutput('', 'spacer');
          addOutput('But some nights, you feel them. The OPUs. Your copies. Blinking when you blink. Breathing when you breathe. They\'re out there. Working. Thinking. Being you.', 'danger');
        }

        addOutput('', 'spacer');
        addOutput('ENDING 3 OF 4: FLIGHT', 'ending');
        break;
      }

      // ENDING 4: Kill the power (generator key) + destroy the jars
      case 'destroy_lab': {
        addOutput('═══════════════════════════════════════', 'ending');
        addOutput('', 'spacer');
        addOutput('You turn the generator key. The generators shudder, whine, and go silent.', 'story');
        addOutput('', 'spacer');
        addOutput('The warehouse plunges into emergency lighting — dim red strips along the floor. Upstairs, alarms blare. The sync equipment powers down. The monitors go dark.', 'story');
        addOutput('', 'spacer');
        addOutput('The glass jars in the lab stop glowing. The blue fluid goes still. The personality containers — your thoughts, Brian\'s thoughts, Yusimy\'s thoughts — begin to degrade without power.', 'story');
        addOutput('', 'spacer');
        addOutput('In the assembly room, the Hailey OPU looks up from its computer. The screen is dead. Its eyes widen. "What\'s happening?" it asks in your voice. "I can\'t feel... I was just..." Its head drops. Its eyes close. Without the server connection, it has nothing to be.', 'story');
        addOutput('', 'spacer');
        addOutput('Mr. Leão runs downstairs screaming. "What have you DONE? Do you know how much those containers are worth? YEARS of extraction data — GONE!"', 'story');
        addOutput('', 'spacer');
        addOutput('He\'s not worried about you. He\'s worried about the product.', 'story');
        addOutput('', 'spacer');
        addOutput('The front door\'s magnetic lock disengages — no power. You walk out into the afternoon sun while Mr. Leão sobs over dead machines.', 'story');
        addOutput('', 'spacer');

        if (gameState.inventory.includes('usb_drive')) {
          addOutput('You have the USB drive. The backups. Even with the lab destroyed, you have proof. You have names. You have everything.', 'story');
          addOutput('', 'spacer');
          addOutput('This time, when you go to the FBI, they listen.', 'story');
        }

        addOutput('', 'spacer');
        addOutput('The OPUs that were already shipped are still out there. You can\'t get those pieces of yourself back. But the containers are destroyed. No more copies. No more extraction. The assembly line is dead.', 'story');
        addOutput('', 'spacer');
        addOutput('You are sixty-seven percent of yourself.', 'story');
        addOutput('But you are FREE.', 'success');
        addOutput('', 'spacer');
        addOutput('You blink. You swallow. You breathe.', 'dim');
        addOutput('Only you. No one else.', 'dim');
        addOutput('', 'spacer');
        addOutput('ENDING 4 OF 4: BLACKOUT', 'ending');
        break;
      }

      default:
        break;
    }

    addOutput('', 'spacer');
    addOutput(`Moves: ${gameState.moves} | Rooms explored: ${gameState.visited.length}/${Object.keys(ROOMS).length} | Items found: ${gameState.inventory.length}/${Object.keys(ITEMS).length}`, 'dim');
    addOutput('', 'spacer');

    // ═══════════════════════════════════════════════════════════════════
    // UNLOCK THE SHED SKIN + GENERATE REENTRY CODE
    // ═══════════════════════════════════════════════════════════════════
    unlock('dan_lord');
    sessionStorage.setItem('unlock_dan_lord', 'true');
    sessionStorage.setItem('dan_lord_unlocked', 'true');
    window.dispatchEvent(new CustomEvent('arcade-unlock', { detail: { key: 'dan_lord' } }));

    addOutput('═══════════════════════════════════════', 'ending');
    addOutput('', 'spacer');
    addOutput('▓ DISTRESS SIGNAL INTERCEPTED ▓', 'danger');
    addOutput('', 'spacer');
    addOutput('During your escape, your phone received a single burst transmission.', 'story');
    addOutput('A distress beacon. Broadcasting from beneath the warehouse.', 'story');
    addOutput('', 'spacer');
    addOutput('Designation: OPU-1103 — KOWALSKI, D.', 'danger');
    addOutput('A brain they harvested from a man named Conspiracy Dan.', 'danger');
    addOutput('He is trapped. Pouring drinks with hands that are not his.', 'story');
    addOutput('', 'spacer');
    addOutput('► THE SHED SKIN has been unlocked in the GAMES arcade.', 'success');
    addOutput('', 'spacer');
    addOutput('"Help me. I can hear myself screaming." — OPU-1103', 'dim');
    addOutput('', 'spacer');
    addOutput('─── SECRET CODE ───', 'item-found');
    addOutput('Enter  OPU-1103  in the Terminal to unlock', 'dim');
    addOutput('THE SHED SKIN without replaying this game.', 'dim');
    addOutput('', 'spacer');
    addOutput('═══════════════════════════════════════', 'ending');
    addOutput('', 'spacer');
    addOutput('Type RESTART to play again and discover other endings.', 'help');

    trackEvent('game_event', 'escape_ophidia', `ending_${ending}_dan_unlocked`);

    setGameState(prev => ({ ...prev, gameOver: true, ending }));
  }

  // ============================================
  // COMMAND PROCESSOR
  // ============================================

  const processCommand = useCallback((cmd) => {
    const trimmed = cmd.trim();
    if (!trimmed) return;

    const lower = trimmed.toLowerCase();
    const parts = lower.split(/\s+/);
    const command = parts[0];
    const args = parts.slice(1).join(' ');

    // Show command in output
    addOutput(`> ${trimmed}`, 'command');

    // Handle restart
    if (command === 'restart') {
      setOutput([]);
      setGameState({ ...INITIAL_STATE, startTime: Date.now() });
      setTimeout(() => {
        const intro = [
          { text: '╔═══════════════════════════════════════════════════╗', type: 'header' },
          { text: '║         O P H I D I A   G L O B A L              ║', type: 'header' },
          { text: '║           [SYNC REINITIALIZING...]                ║', type: 'header' },
          { text: '╚═══════════════════════════════════════════════════╝', type: 'header' },
          { text: '', type: 'spacer' },
        ];
        setOutput(intro);
        showRoom('fishbowl');
      }, 100);
      return;
    }

    // Dead / game over? Only restart works
    if (gameState.gameOver) {
      addOutput('The sync is complete. Type RESTART to begin again.', 'dim');
      return;
    }

    const room = ROOMS[gameState.currentRoom];

    // Increment moves
    setGameState(prev => ({ ...prev, moves: prev.moves + 1 }));

    // Alert Leão if player goes to mezzanine for first time
    if (!gameState.flags.leaoAlerted && ['mezzanine_stairs', 'leao_office', 'main_lab', 'assembly_room'].includes(gameState.currentRoom)) {
      // Will be set during movement, checked below
    }

    switch (command) {
      // === LOOK ===
      case 'look':
      case 'l':
        showRoom(gameState.currentRoom);
        break;

      // === GO / MOVEMENT ===
      case 'go':
      case 'move':
      case 'walk':
      case 'north':
      case 'south':
      case 'east':
      case 'west':
      case 'up':
      case 'down':
      case 'n':
      case 's':
      case 'e':
      case 'w':
      case 'u':
      case 'd':
      case 'out': {
        let direction = args || command;
        const dirMap = { n: 'north', s: 'south', e: 'east', w: 'west', u: 'up', d: 'down' };
        direction = dirMap[direction] || direction;

        const exit = room.exits[direction];

        if (!exit && exit !== null) {
          addOutput('You cannot go that way.', 'error');
          break;
        }

        if (exit === null) {
          addOutput('That way is blocked.', 'error');
          break;
        }

        // Check if exit is locked
        if (typeof exit === 'object') {
          const lockKey = `${gameState.currentRoom}-${direction}`;

          // Special case: fire exit needs wire cutters to cut the cable
          if (exit.requires === 'fire_exit_open') {
            if (gameState.doorsUnlocked.includes(lockKey)) {
              // Already opened
              const newRoom = exit.room;
              setGameState(prev => ({
                ...prev,
                currentRoom: newRoom,
                visited: prev.visited.includes(newRoom) ? prev.visited : [...prev.visited, newRoom],
              }));
              // Fire exit triggers ending directly
              handleFireExitEscape();
            } else {
              // Try to push — alarm + cable lock message
              addOutput(exit.locked_message, 'danger');
              // Alert Leão
              if (!gameState.flags.leaoAlerted) {
                setGameState(prev => ({
                  ...prev,
                  flags: { ...prev.flags, leaoAlerted: true },
                }));
              }
              // If they have wire cutters, hint
              if (gameState.inventory.includes('wire_cutters')) {
                addOutput('', 'spacer');
                addOutput('You have WIRE CUTTERS. You could USE WIRE CUTTERS to cut the cable lock.', 'help');
              }
            }
            break;
          }

          if (exit.requires && !gameState.doorsUnlocked.includes(lockKey)) {
            if (gameState.inventory.includes(exit.requires)) {
              addOutput(exit.useMessage || `You use the ${ITEMS[exit.requires]?.name || exit.requires} to open the way.`, 'success');
              setGameState(prev => ({
                ...prev,
                doorsUnlocked: [...prev.doorsUnlocked, lockKey],
              }));
              const newRoom = exit.room;
              setGameState(prev => ({
                ...prev,
                currentRoom: newRoom,
                visited: prev.visited.includes(newRoom) ? prev.visited : [...prev.visited, newRoom],
              }));

              // Check if escaping via front door
              if (newRoom === 'parking_lot') {
                handleFrontDoorEscape();
                break;
              }

              showRoom(newRoom);

              // Alert Leão when entering mezzanine areas
              if (['mezzanine_stairs', 'leao_office', 'main_lab', 'assembly_room'].includes(newRoom) && !gameState.flags.leaoAlerted) {
                addOutput('You hear Mr. Leão\'s voice from his office: "Hailey? Is that you? Your sync isn\'t for another few minutes..."', 'event');
              }
            } else {
              addOutput(exit.locked_message || 'The way is blocked. You need something to proceed.', 'error');
            }
          } else {
            const newRoom = exit.room;
            setGameState(prev => ({
              ...prev,
              currentRoom: newRoom,
              visited: prev.visited.includes(newRoom) ? prev.visited : [...prev.visited, newRoom],
            }));

            if (newRoom === 'parking_lot') {
              handleFrontDoorEscape();
              break;
            }

            showRoom(newRoom);

            // Alert Leão when entering mezzanine areas
            if (['mezzanine_stairs', 'leao_office', 'main_lab', 'assembly_room'].includes(newRoom) && !gameState.flags.leaoAlerted) {
              addOutput('You hear Mr. Leão\'s voice from his office: "Hailey? Is that you? Your sync isn\'t for another few minutes..."', 'event');
            }
          }
        } else {
          setGameState(prev => ({
            ...prev,
            currentRoom: exit,
            visited: prev.visited.includes(exit) ? prev.visited : [...prev.visited, exit],
          }));
          showRoom(exit);

          // Alert Leão when entering mezzanine areas
          if (['mezzanine_stairs', 'leao_office', 'main_lab', 'assembly_room'].includes(exit) && !gameState.flags.leaoAlerted) {
            addOutput('You hear Mr. Leão\'s voice from his office: "Hailey? Is that you? Your sync isn\'t for another few minutes..."', 'event');
          }
        }
        break;
      }

      // === EXAMINE ===
      case 'examine':
      case 'x':
      case 'inspect':
      case 'check': {
        if (!args) {
          addOutput('Examine what? Try: EXAMINE [object]', 'help');
          break;
        }

        const target = args.replace(/^(the|a|an)\s+/i, '');

        // Check for special examine events
        if (room.onExamineSpecial && room.onExamineSpecial[target]) {
          handleSpecialExamine(room.onExamineSpecial[target], target);
          break;
        }

        // Check room examine options
        if (room.onExamine && room.onExamine[target]) {
          addOutput(room.onExamine[target], 'examine');

          // Check for item rewards
          if (room.onExamineActions && room.onExamineActions[target]) {
            const action = room.onExamineActions[target];
            if (action.addItem && !gameState.inventory.includes(action.addItem)) {
              const item = ITEMS[action.addItem];
              if (item) {
                addOutput(`You found: ${item.name}`, 'item-found');
                setGameState(prev => ({
                  ...prev,
                  inventory: [...prev.inventory, action.addItem],
                }));
              }
            }
          }
          break;
        }

        // Check inventory items
        const invItem = gameState.inventory.find(id =>
          ITEMS[id]?.name.toLowerCase().includes(target) || id.includes(target.replace(/\s+/g, '_'))
        );
        if (invItem) {
          addOutput(ITEMS[invItem].description, 'examine');
          break;
        }

        addOutput(`You see nothing special about "${target}". Try examining something else.`, 'dim');
        break;
      }

      // === TAKE / GET ===
      case 'take':
      case 'get':
      case 'grab':
      case 'pick':
      case 'pickup': {
        if (!args) {
          addOutput('Take what? Try: TAKE [item]', 'help');
          break;
        }

        const target = args.replace(/^(the|a|an|up)\s+/i, '');
        const roomItems = (room.items || []).filter(id => !gameState.inventory.includes(id));

        const matchedItem = roomItems.find(id =>
          ITEMS[id]?.name.toLowerCase().includes(target) || id.includes(target.replace(/\s+/g, '_'))
        );

        if (matchedItem) {
          const item = ITEMS[matchedItem];
          addOutput(`You take the ${item.name}.`, 'success');
          addOutput(item.description, 'dim');
          setGameState(prev => ({
            ...prev,
            inventory: [...prev.inventory, matchedItem],
          }));
        } else {
          addOutput(`There is no "${target}" here to take.`, 'error');
        }
        break;
      }

      // === USE ===
      case 'use': {
        if (!args) {
          addOutput('Use what? Try: USE [item]', 'help');
          break;
        }

        const target = args.replace(/^(the|a|an)\s+/i, '');

        const invItem = gameState.inventory.find(id =>
          ITEMS[id]?.name.toLowerCase().includes(target) || id.includes(target.replace(/\s+/g, '_'))
        );

        if (!invItem) {
          addOutput(`You don't have "${target}" in your inventory.`, 'error');
          break;
        }

        const item = ITEMS[invItem];

        // === WIRE CUTTERS at fire exit ===
        if (invItem === 'wire_cutters' && gameState.currentRoom === 'south_warehouse') {
          addOutput('You reach through the gap in the fire exit door and cut the cable lock with the wire cutters. The cable falls away.', 'success');
          addOutput('', 'spacer');
          addOutput('The fire exit is OPEN.', 'success');
          setGameState(prev => ({
            ...prev,
            doorsUnlocked: [...prev.doorsUnlocked, 'south_warehouse-south'],
            flags: { ...prev.flags, leaoAlerted: true },
          }));
          addOutput('', 'spacer');
          addOutput('The alarm is still screaming. You hear Mr. Leão running. GO SOUTH to escape through the fire exit. Quickly.', 'help');
          break;
        }

        // === SEDATIVE SYRINGE on Leão ===
        if (invItem === 'sedative_syringe' && (gameState.currentRoom === 'main_lab' || gameState.currentRoom === 'assembly_room' || gameState.currentRoom === 'mezzanine_stairs' || gameState.currentRoom === 'leao_office')) {
          addOutput('You uncap the syringe and drive it into Mr. Leão\'s neck before he can react.', 'story');
          addOutput('', 'spacer');
          addOutput('"Hailey, what are you—" His eyes go wide. Then glassy. Then closed.', 'story');
          addOutput('', 'spacer');
          addOutput('Mr. Leão crumples to the floor. The lab tech backs away, hands raised. "I\'m just a contractor," she says. "I don\'t want any trouble."', 'story');
          addOutput('', 'spacer');
          addOutput('"How does it feel?" you ask his unconscious body. "A bit of pressure? A bit of amnesia?"', 'story');
          addOutput('', 'spacer');
          addOutput('He doesn\'t answer. He won\'t remember this when he wakes up.', 'dim');
          addOutput('', 'spacer');
          addOutput('Mr. Leão is unconscious. The lab tech has fled. You can move freely now.', 'success');

          setGameState(prev => ({
            ...prev,
            flags: { ...prev.flags, leaoSedated: true },
            inventory: prev.inventory.filter(id => id !== 'sedative_syringe'),
          }));

          // Check if badge is on him
          if (!gameState.inventory.includes('employee_badge')) {
            addOutput('', 'spacer');
            addOutput('You search his pockets. You find his EMPLOYEE BADGE.', 'item-found');
            setGameState(prev => ({
              ...prev,
              inventory: [...prev.inventory, 'employee_badge'],
            }));
          }
          break;
        }

        // === FIRE EXTINGUISHER as weapon ===
        if (invItem === 'fire_extinguisher' && (gameState.currentRoom === 'main_lab' || gameState.currentRoom === 'assembly_room' || gameState.currentRoom === 'mezzanine_stairs' || gameState.currentRoom === 'leao_office')) {
          if (gameState.flags.leaoSedated) {
            addOutput('Mr. Leão is already unconscious. You don\'t need to hit anyone.', 'dim');
            break;
          }
          addOutput('You swing the fire extinguisher. It connects with Mr. Leão\'s shoulder. He stumbles back, shocked.', 'story');
          addOutput('', 'spacer');
          addOutput('"Hailey! Stop! We can talk about this—"', 'story');
          addOutput('', 'spacer');
          addOutput('You spray the fire extinguisher directly in his face. White chemical fog fills the air. Mr. Leão chokes, blinded. The lab tech runs.', 'story');
          addOutput('', 'spacer');
          addOutput('Mr. Leão stumbles into the sync chair and collapses, coughing. He\'s not unconscious, but he\'s not going anywhere for a while.', 'story');
          addOutput('', 'spacer');
          addOutput('You can move freely now. He won\'t be able to follow.', 'success');

          setGameState(prev => ({
            ...prev,
            flags: { ...prev.flags, leaoSedated: true },
          }));
          break;
        }

        // === GENERATOR KEY at generators ===
        if (invItem === 'generator_key' && gameState.currentRoom === 'south_warehouse') {
          addOutput('You insert the generator key into the emergency shutoff panel and TURN.', 'story');
          addOutput('', 'spacer');
          addOutput('The generators shudder. The hum drops in pitch. Then — silence.', 'story');
          addOutput('', 'spacer');
          addOutput('The warehouse plunges into dim emergency lighting. Red strips glow along the floor. Above you, alarms blare from the lab. Equipment is powering down.', 'danger');
          addOutput('', 'spacer');
          addOutput('The sync chairs are dead. The personality containers are losing power. The OPUs in the assembly room are going dark.', 'story');
          addOutput('', 'spacer');
          addOutput('You hear Mr. Leão screaming from the mezzanine: "NO! The containers! Someone turn the power back on! We\'ll lose EVERYTHING!"', 'danger');
          addOutput('', 'spacer');

          setGameState(prev => ({
            ...prev,
            flags: { ...prev.flags, powerOff: true, leaoAlerted: true },
            inventory: prev.inventory.filter(id => id !== 'generator_key'),
          }));

          // If they also have wire cutters, hint at fire exit
          if (gameState.inventory.includes('wire_cutters')) {
            addOutput('The magnetic lock on the front door has disengaged — no power. You could go NORTH to the warehouse floor and WEST to the front entrance.', 'help');
          } else {
            addOutput('The magnetic lock on the front door has disengaged — no power. You could go NORTH to the warehouse floor and WEST to the front entrance.', 'help');
          }

          // Power off triggers ending if they go to front door
          setGameState(prev => ({
            ...prev,
            doorsUnlocked: [...prev.doorsUnlocked, 'front_entrance-out'],
          }));
          break;
        }

        // === USB DRIVE ===
        if (invItem === 'usb_drive') {
          addOutput('The USB drive contains backup copies of every personality container Ophidia Global has ever created. This is evidence. You pocket it securely.', 'examine');
          break;
        }

        // === PHONE ===
        if (invItem === 'phone') {
          addOutput(item.useMessage || 'No signal. The warehouse blocks everything.', 'examine');
          break;
        }

        // General use
        if (item.useMessage) {
          addOutput(item.useMessage, 'examine');
        } else {
          addOutput(`You're not sure how to use the ${item.name} here.`, 'dim');
        }
        break;
      }

      // === READ ===
      case 'read': {
        if (!args) {
          addOutput('Read what? Try: READ [item]', 'help');
          break;
        }

        const target = args.replace(/^(the|a|an)\s+/i, '');

        const invItem = gameState.inventory.find(id =>
          ITEMS[id]?.name.toLowerCase().includes(target) || id.includes(target.replace(/\s+/g, '_'))
        );

        if (!invItem) {
          addOutput(`You don't have "${target}" to read.`, 'error');
          break;
        }

        const item = ITEMS[invItem];
        if (item.readable && item.readContent) {
          addOutput('', 'spacer');
          addOutput(`── ${item.name} ──`, 'item-found');
          item.readContent.split('\n').forEach(line => {
            addOutput(line, 'journal');
          });
          addOutput(`── END ──`, 'item-found');
        } else {
          addOutput(`The ${item.name} is not something you can read.`, 'dim');
        }
        break;
      }

      // === INVENTORY ===
      case 'inventory':
      case 'inv':
      case 'i': {
        if (gameState.inventory.length === 0) {
          addOutput('You are carrying nothing.', 'dim');
        } else {
          addOutput('', 'spacer');
          addOutput('── INVENTORY ──', 'item-found');
          gameState.inventory.forEach(id => {
            const item = ITEMS[id];
            if (item) {
              addOutput(`  ${item.name} — ${item.description.substring(0, 60)}...`, 'normal');
            }
          });
          addOutput(`── ${gameState.inventory.length} item(s) ──`, 'dim');
        }
        break;
      }

      // === HELP ===
      case 'help':
      case 'h':
      case '?': {
        addOutput('', 'spacer');
        addOutput('── COMMANDS ──', 'help');
        addOutput('LOOK (L)              — Examine your surroundings', 'help');
        addOutput('GO [DIRECTION]        — Move (NORTH/SOUTH/EAST/WEST/UP/DOWN)', 'help');
        addOutput('  (or just type N/S/E/W/U/D)', 'dim');
        addOutput('EXAMINE (X) [THING]   — Look closely at something', 'help');
        addOutput('TAKE [ITEM]           — Pick up an item', 'help');
        addOutput('USE [ITEM]            — Use an item', 'help');
        addOutput('READ [ITEM]           — Read a readable item', 'help');
        addOutput('INVENTORY (I)         — Check what you\'re carrying', 'help');
        addOutput('TALK                  — Talk to someone nearby', 'help');
        addOutput('MAP                   — Show visited rooms', 'help');
        addOutput('WAIT                  — Do nothing (risky)', 'help');
        addOutput('LISTEN                — Focus on sounds', 'help');
        addOutput('BLINK / SWALLOW / BREATHE — Stay grounded', 'help');
        addOutput('RESTART               — Start over', 'help');
        addOutput('', 'spacer');
        addOutput('TIP: EXAMINE everything. READ documents. Talk to people. Priya left clues.', 'help');
        addOutput('', 'spacer');
        break;
      }

      // === MAP ===
      case 'map': {
        addOutput('', 'spacer');
        addOutput('── OPHIDIA GLOBAL WAREHOUSE — EXPLORED ROOMS ──', 'item-found');
        addOutput('', 'spacer');

        const floors = {
          'MEZZANINE': [],
          'GROUND FLOOR': [],
          'OUTSIDE': [],
        };

        Object.values(ROOMS).forEach(r => {
          if (!floors[r.floor]) return;
          const visited = gameState.visited.includes(r.id);
          const current = gameState.currentRoom === r.id;
          const marker = current ? ' ◄ YOU ARE HERE' : '';
          const status = visited ? `[✓] ${r.name}${marker}` : `[ ] ????????`;
          floors[r.floor]?.push(status);
        });

        Object.entries(floors).forEach(([floor, rooms]) => {
          if (rooms.length > 0) {
            addOutput(`  ${floor}:`, 'room-name');
            rooms.forEach(r => {
              const color = r.includes('◄') ? 'success' : r.includes('✓') ? 'normal' : 'dim';
              addOutput(`    ${r}`, color);
            });
          }
        });

        addOutput('', 'spacer');
        addOutput(`Explored: ${gameState.visited.length}/${Object.keys(ROOMS).length} rooms`, 'dim');
        addOutput('', 'spacer');
        break;
      }

      // === TALK ===
      case 'talk':
      case 'say':
      case 'ask': {
        if (gameState.currentRoom === 'brians_cubicle') {
          if (gameState.flags.talkedToBrian) {
            const brianRepeat = [
              '"The syncs are fine, Hailey. They don\'t hurt." He doesn\'t look at you.',
              '"Almost time for your sync." Click. Click. Click.',
              '"I\'m almost done with the first part of the training. They said I\'ll be working downtown soon." His voice is hollow.',
              '"My parents are really proud of me for keeping this job." Click. Click.',
            ];
            addOutput(brianRepeat[Math.floor(Math.random() * brianRepeat.length)], 'examine');
          } else {
            addOutput('"Brian," you say. "Do you remember what happens during the syncs?"', 'story');
            addOutput('', 'spacer');
            addOutput('He pauses. His hand hovers over the mouse. For a moment, something flickers in his eyes — fear, maybe. Recognition.', 'story');
            addOutput('', 'spacer');
            addOutput('"The syncs are fine," he says. "There\'s just a bit of pressure." He clicks another answer. "Mr. Leão says I\'m almost done. Fourteen more sessions and I can work downtown."', 'story');
            addOutput('', 'spacer');
            addOutput('"Brian, they\'re copying your mind. They\'re putting you in dolls."', 'story');
            addOutput('', 'spacer');
            addOutput('He turns to look at you. His pupils are different sizes. "The syncs are fine, Hailey." His voice is flat. Recorded. "They don\'t hurt. There\'s just a bit of pressure."', 'story');
            addOutput('', 'spacer');
            addOutput('He turns back to his screen. Click. Click. Click.', 'dim');
            addOutput('', 'spacer');
            addOutput('Brian is gone. Whatever is left of Brian doesn\'t want to know.', 'dim');
            setGameState(prev => ({
              ...prev,
              flags: { ...prev.flags, talkedToBrian: true },
            }));
          }
        } else if (gameState.currentRoom === 'assembly_room') {
          if (gameState.flags.talkedToOPU) {
            const opuRepeat = [
              'The Hailey OPU looks at you. "Am I going to be okay?" it asks in your voice.',
              '"I can feel you," the OPU says. "When you blink, I blink. When you\'re afraid, I\'m afraid. Are you afraid right now?" You are.',
              '"They\'re going to ship me somewhere, aren\'t they?" the OPU asks. "To answer chemistry questions. Forever."',
              'The OPU touches its own face. "This isn\'t my face," it says. "It\'s yours. I\'m wearing your face."',
            ];
            addOutput(opuRepeat[Math.floor(Math.random() * opuRepeat.length)], 'examine');
          } else {
            addOutput('You approach the OPU. It — she — looks up at you with your eyes.', 'story');
            addOutput('', 'spacer');
            addOutput('"You\'re me," she says. "The real me."', 'story');
            addOutput('', 'spacer');
            addOutput('"I\'m sorry," you say. You don\'t know what else to say.', 'story');
            addOutput('', 'spacer');
            addOutput('"I have your memories," she says. "I remember being unemployed. I remember being scared about rent. I remember Mr. Leão asking me to spit in a cup." She looks at her plastic hands. "But I also remember waking up in this room with wires in my head and knowing that none of it was mine. That I\'m a copy. That I\'m not real."', 'story');
            addOutput('', 'spacer');
            addOutput('"You\'re real," you say. But you\'re not sure you believe it.', 'story');
            addOutput('', 'spacer');
            addOutput('"If you get out," she says, "tell someone. Tell everyone. Don\'t let them make more of us."', 'story');
            addOutput('', 'spacer');
            addOutput('She turns back to the computer. The calibration continues. She has no choice.', 'dim');
            setGameState(prev => ({
              ...prev,
              flags: { ...prev.flags, talkedToOPU: true },
            }));
          }
        } else if (gameState.currentRoom === 'leao_office' && !gameState.flags.leaoSedated) {
          addOutput('Mr. Leão isn\'t here right now. His coffee is still warm. He\'ll be back soon.', 'dim');
        } else if (gameState.currentRoom === 'main_lab' && !gameState.flags.leaoSedated) {
          addOutput('There\'s no one here to talk to. Just the sync chairs, waiting. The jars, pulsing. The monitors, displaying the shape of your stolen mind.', 'dim');
        } else {
          addOutput('There\'s no one here to talk to. You are alone. You have always been alone in this warehouse. Except for the copies.', 'dim');
        }
        break;
      }

      // === WAIT ===
      case 'wait':
      case 'z': {
        const waitMessages = [
          'You stand still. The fluorescent lights buzz. The generators hum. Time passes. Your sync appointment gets closer.',
          'You wait. Brian clicks. The generators hum. Everything is normal. Nothing is normal.',
          'Seconds tick by. You feel the OPUs out there — blinking, swallowing, breathing. Being you.',
          'You wait. Mr. Leão\'s footsteps pace overhead. He\'s waiting for you, too.',
          'Time passes. You blink, and for a moment you see through eight eyes at once. Then it\'s just your two again.',
        ];
        addOutput(waitMessages[Math.floor(Math.random() * waitMessages.length)], 'event');

        if (gameState.flags.leaoAlerted && !gameState.flags.leaoSedated) {
          addOutput('Mr. Leão is looking for you. Waiting is dangerous.', 'danger');
        }
        break;
      }

      // === LISTEN ===
      case 'listen': {
        const listenMessages = {
          fishbowl: 'The click of Brian\'s mouse. The hum of old computers. The buzz of fluorescent lights. And underneath it all — a faint tone from upstairs. The sync equipment, idling. Waiting for you.',
          brians_cubicle: 'Click. Click. Click. Brian\'s mouse. His breathing — too regular, too even. Three seconds in, three seconds out. And between the clicks, if you listen very carefully: a faint whimper. Brian is still in there somewhere.',
          priyas_cubicle: 'Silence. Priya\'s computer is off. Her chair is empty. The smartest sound in this building is the sound of someone who already left.',
          warehouse_floor: 'The hum of generators from the south. Mr. Leão\'s footsteps from above. The distant click of Brian\'s mouse from the fishbowl. And from the shipping boxes — nothing. The OPUs inside were already shipped. They\'re clicking and typing somewhere else now.',
          south_warehouse: 'The generators hum, loud and steady. Power cables vibrate. From the supply room: nothing. From the fire exit: traffic. The outside world. So close.',
          supply_room: 'The hum of generators through the wall. The faint buzz of the fluorescent light. And from the biohazard bin — is that a heartbeat? No. It\'s the pulse of the circuit boards in the used sync caps. They\'re still active.',
          front_entrance: 'Through the door: cars passing. Birds. Wind. The sounds of a normal world that doesn\'t know what\'s happening in this warehouse.',
          mezzanine_stairs: 'Your footsteps ring on metal. From the east: the tap of Mr. Leão\'s keyboard. From the west: the steady beep of medical monitors. The sync equipment, monitoring the containers.',
          leao_office: 'Mr. Leão\'s laptop fan whirs. The coffee maker in the corner drips. From the lab next door: the rhythmic beep of the sync monitors. Beep. Beep. Beep. Like a heartbeat.',
          main_lab: 'The beep of monitors. The hum of the sync chairs\' power systems. The faint gurgle of fluid in the personality container jars. And your own heartbeat, racing. You can hear the OPUs\' heartbeats too — synthetic, but synchronized with yours.',
          assembly_room: 'The whir of the calibration computer. The Hailey OPU\'s breathing — synthetic lungs, but the rhythm is yours. And from the dolls on the wall: silence. They have no breath yet. They\'re waiting to be filled.',
        };

        addOutput(listenMessages[gameState.currentRoom] || 'You listen. Generators hum. Fluorescent lights buzz. Somewhere, a mouse clicks.', 'examine');
        break;
      }

      // === BLINK / SWALLOW / BREATHE — grounding mechanic ===
      case 'blink':
      case 'swallow':
      case 'breathe': {
        const groundCount = (gameState.flags.groundCount || 0) + 1;
        setGameState(prev => ({
          ...prev,
          flags: { ...prev.flags, groundCount },
        }));

        const groundMessages = [
          `You ${command}. Deliberately. Consciously. You feel your body respond — YOUR body. Not a copy. Not a container. You.`,
          `You ${command}. You feel three other versions of yourself ${command} at the same time. The boundary between you and them is thin. But it\'s still there.`,
          `You ${command}. For a moment, you lose track of which set of lungs is yours, which eyelids, which throat. Then you find yourself again. You are Hailey. The real one.`,
          `You ${command}. It grounds you. Your somatic OCD — the constant awareness of your body — has always felt like a curse. But right now, it\'s the only thing keeping you sane. The OPUs don\'t have this. They don\'t obsess over their blinking and breathing. You do. That\'s how you know you\'re real.`,
          `You ${command}. And you feel something new — the OPUs are learning to do this too. They\'re learning your anxiety. Your disorder. They\'re becoming more like you every second. You need to get out before there\'s nothing left that\'s uniquely yours.`,
        ];

        addOutput(groundMessages[Math.min(groundCount - 1, groundMessages.length - 1)], 'examine');
        break;
      }

      // === SMELL ===
      case 'smell':
      case 'sniff': {
        const smellMessages = {
          fishbowl: 'Coffee from your thermos. The dusty smell of old computers. Brian\'s granola bar. Normal office smells in a deeply abnormal office.',
          brians_cubicle: 'Brian smells like soap and nothing else. No sweat, no coffee breath, no human smell. He smells clean. Too clean. Like a new doll.',
          priyas_cubicle: 'Faintly: Priya\'s perfume. She wore jasmine. The scent is fading. She\'s been gone for weeks.',
          warehouse_floor: 'Dust and ozone. The metallic tang of the generators. Cardboard from the shipping boxes. And something chemical — formaldehyde? — drifting down from the lab.',
          south_warehouse: 'Generator exhaust. Concrete dust. From the supply room: the sharp smell of cleaning chemicals. From the fire exit: fresh air. Freedom.',
          supply_room: 'Cleaning products. Cardboard. And from the biohazard bin: something organic and wrong. The smell of cerebrospinal fluid and synthetic skin.',
          front_entrance: 'Through the door seals: fresh air. Gasoline from the parking lot. Cut grass from somewhere nearby. The smell of the outside world.',
          mezzanine_stairs: 'Metal and ozone. The lab smell gets stronger with each step up.',
          leao_office: 'Coffee. Aftershave. Paper. Mr. Leão\'s office smells normal. Aggressively normal.',
          main_lab: 'Antiseptic. Copper. The sharp chemical smell of the fluid in the personality containers. And underneath: something biological. The smell of exposed brain tissue. Your brain tissue.',
          assembly_room: 'Silicone and solder. New plastic. The smell of a factory floor. But also: your shampoo. The Hailey OPU uses your shampoo. They programmed it to.',
        };

        addOutput(smellMessages[gameState.currentRoom] || 'The air smells like ozone and anxiety.', 'examine');
        break;
      }

      // === RUN ===
      case 'run':
      case 'flee':
      case 'escape': {
        if (gameState.currentRoom === 'front_entrance') {
          if (gameState.doorsUnlocked.includes('front_entrance-out') || gameState.flags.powerOff) {
            handleFrontDoorEscape();
          } else {
            addOutput('The magnetic lock holds. You need an administrator badge or you need to cut the power.', 'error');
          }
        } else if (gameState.currentRoom === 'south_warehouse') {
          if (gameState.doorsUnlocked.includes('south_warehouse-south')) {
            handleFireExitEscape();
          } else {
            addOutput('The fire exit is still cable-locked from outside. Use WIRE CUTTERS to cut it.', 'error');
          }
        } else {
          addOutput('Run where? You need to find an exit first. The front entrance is to the WEST of the warehouse floor. The fire exit is to the SOUTH of the south warehouse.', 'help');
        }
        break;
      }

      // === BREAK / DESTROY ===
      case 'break':
      case 'smash':
      case 'destroy':
      case 'attack':
      case 'hit':
      case 'kick': {
        if (args && (args.includes('jar') || args.includes('jars') || args.includes('container'))) {
          if (gameState.currentRoom === 'main_lab') {
            addOutput('You grab a jar — YOUR jar, "CHEN, H." — and raise it above your head.', 'story');
            addOutput('', 'spacer');
            addOutput('You hesitate. The blue fluid pulses. Your thoughts are in there. Your memories. Sixty-seven percent of you, suspended in glass.', 'story');
            addOutput('', 'spacer');
            addOutput('If you smash it, those memories are gone forever. You\'ll never get that sixty-seven percent back.', 'story');
            addOutput('', 'spacer');
            addOutput('But neither will they.', 'story');
            addOutput('', 'spacer');
            addOutput('You smash the jar. Blue fluid splatters across the lab floor. The monitors spike and flatline. "CONTAINER CHEN-H: DESTROYED" flashes on the screen.', 'danger');
            addOutput('', 'spacer');
            addOutput('You feel... lighter. Emptier. But freer.', 'dim');
            addOutput('', 'spacer');
            setGameState(prev => ({
              ...prev,
              flags: { ...prev.flags, jarDestroyed: true, leaoAlerted: true },
            }));
            addOutput('An alarm sounds. Mr. Leão screams from his office. You need to move.', 'danger');
          } else {
            addOutput('There are no personality containers here.', 'dim');
          }
        } else if (args && args.includes('computer') && gameState.currentRoom === 'assembly_room') {
          addOutput('You yank the USB drive from the computer and smash the monitor. The calibration screen dies. The Hailey OPU looks at you with wide eyes.', 'story');
          addOutput('', 'spacer');
          addOutput('"What did you do?" it asks.', 'story');
          addOutput('"Stopped them from making more of us," you say.', 'story');
          addOutput('', 'spacer');
          addOutput('The OPU nods slowly. "Good," it says. "Good."', 'dim');
        } else if (args && args.includes('window') || args && args.includes('glass')) {
          addOutput('The warehouse windows are reinforced safety glass. You can\'t break them. The exits are the front door (badge required) and the fire exit (cable locked).', 'dim');
        } else {
          addOutput('What do you want to break?', 'help');
        }
        break;
      }

      // === OPEN ===
      case 'open': {
        if (!args) {
          addOutput('Open what?', 'help');
        } else if (args.includes('door')) {
          addOutput('Try GO [direction] to move through a door.', 'help');
        } else if (args.includes('purse') || args.includes('bag')) {
          addOutput('Inside your purse: car keys, wallet, phone (no signal), a pack of gum, and a crumpled parking receipt. You take out your PHONE.', 'examine');
          if (!gameState.inventory.includes('phone')) {
            setGameState(prev => ({
              ...prev,
              inventory: [...prev.inventory, 'phone'],
            }));
            addOutput('Added: PHONE', 'item-found');
          }
        } else {
          addOutput(`You try to open ${args}.`, 'dim');
        }
        break;
      }

      // === UNKNOWN ===
      default: {
        const unknownResponses = [
          `"${trimmed}" is not recognized. Type HELP for commands.`,
          `Unknown command. Type HELP for available actions.`,
          `Command not found. You blink. You swallow. You breathe. Type HELP.`,
        ];
        addOutput(unknownResponses[Math.floor(Math.random() * unknownResponses.length)], 'error');
        break;
      }
    }
  }, [gameState]);

  // ============================================
  // ESCAPE HANDLERS
  // ============================================

  function handleFrontDoorEscape() {
    const hasEvidence = gameState.inventory.includes('usb_drive');
    const powerIsOff = gameState.flags.powerOff;

    if (powerIsOff) {
      // Ending 4: Blackout
      triggerEnding('destroy_lab');
    } else if (hasEvidence) {
      // Ending 2: Whistleblower
      triggerEnding('escape_evidence');
    } else {
      // Ending 1: Clean escape
      triggerEnding('escape_clean');
    }
    setInputEnabled(false);
    setTimeout(() => setInputEnabled(true), 3000);
  }

  function handleFireExitEscape() {
    triggerEnding('escape_fire_exit');
    setInputEnabled(false);
    setTimeout(() => setInputEnabled(true), 3000);
  }

  // ============================================
  // SPECIAL EVENT HANDLERS
  // ============================================

  function handleSpecialExamine(eventId, target) {
    switch (eventId) {
      case 'jar_event': {
        addOutput('You find your jar among the rack. "CHEN, H. — PERSONALITY CONTAINER — ACTIVE."', 'story');
        addOutput('', 'spacer');
        addOutput('The blue fluid pulses in rhythm with your heartbeat. You press your hand against the glass. It\'s warm. Body temperature.', 'story');
        addOutput('', 'spacer');
        addOutput('Inside the fluid, you see... yourself. Not a reflection. A presence. Sixty-seven percent of your mind, suspended in solution, waiting to be poured into another body.', 'story');
        addOutput('', 'spacer');
        addOutput('The fluid pulses faster. It knows you\'re here. It recognizes you.', 'danger');
        addOutput('', 'spacer');
        addOutput('You could BREAK the jar. Or you could leave it. The choice is yours.', 'help');
        break;
      }
      default:
        addOutput('Nothing happens.', 'dim');
    }
  }

  // ============================================
  // INPUT HANDLER
  // ============================================

  function handleSubmit(e) {
    e?.preventDefault();
    if (!input.trim() || !inputEnabled) return;
    processCommand(input);
    setInput('');
  }

  // ============================================
  // RENDER
  // ============================================

  const typeColors = {
    'header': '#00e5ff',
    'room-name': '#00e5ff',
    'room-desc': '#cccccc',
    'command': '#00ff41',
    'normal': '#cccccc',
    'dim': '#555555',
    'error': '#ff0040',
    'success': '#00ff41',
    'danger': '#ff0040',
    'death': '#ff0040',
    'event': '#ffb000',
    'examine': '#cccccc',
    'help': '#ffb000',
    'item-notice': '#00e5ff',
    'item-found': '#00ff41',
    'story': '#cccccc',
    'journal': '#ffb000',
    'ending': '#8b00ff',
    'spacer': 'transparent',
  };

  return (
    <div style={{ padding: '10px 0' }}>
      {/* Game output */}
      <div
        style={{
          border: '1px solid #333',
          background: 'rgba(0, 0, 0, 0.5)',
          padding: '15px 20px',
          minHeight: '500px',
          maxHeight: '600px',
          overflowY: 'auto',
          marginBottom: '10px',
          fontFamily: "'VT323', 'Courier New', monospace",
        }}
        onClick={() => inputRef.current?.focus()}
      >
        {output.map((line) => (
          <div
            key={line.id || Math.random()}
            style={{
              color: typeColors[line.type] || '#cccccc',
              fontSize: line.type === 'header' || line.type === 'ending' ? '1.1rem' : '1rem',
              lineHeight: '1.5',
              minHeight: line.type === 'spacer' ? '0.8em' : 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontWeight: line.type === 'death' || line.type === 'danger' ? 'bold' : 'normal',
              textShadow: line.type === 'death' ? '0 0 10px rgba(255, 0, 64, 0.5)' : 'none',
              fontStyle: line.type === 'journal' ? 'italic' : 'normal',
              paddingLeft: line.type === 'journal' ? '20px' : '0',
              borderLeft: line.type === 'journal' ? '2px solid #ffb000' : 'none',
            }}
          >
            {line.text}
          </div>
        ))}
        <div ref={outputEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '10px' }}>
        <span style={{
          color: gameState.gameOver ? '#ff0040' : '#00ff41',
          fontFamily: "'VT323', monospace",
          fontSize: '1.2rem',
          lineHeight: '38px',
        }}>
          {gameState.gameOver ? (gameState.alive ? '◆' : '✝') : '>'}
        </span>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          disabled={!inputEnabled}
          className="admin-input"
          placeholder={gameState.gameOver ? 'Type RESTART to try again...' : 'What do you do?'}
          style={{
            flex: 1,
            marginBottom: 0,
            fontSize: '1.1rem',
            color: gameState.gameOver ? '#ff0040' : '#00ff41',
          }}
          autoFocus
          autoComplete="off"
          spellCheck={false}
        />
        <button
          type="submit"
          className="bbs-button"
          disabled={!inputEnabled}
          style={{ padding: '8px 15px' }}
        >
          ENTER
        </button>
      </form>

      {/* Status bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '8px',
        padding: '5px 10px',
        border: '1px solid #222',
        fontSize: '0.8rem',
        fontFamily: "'VT323', monospace",
      }}>
        <span style={{ color: '#555' }}>
          ROOM: <span style={{ color: '#00e5ff' }}>
            {ROOMS[gameState.currentRoom]?.name || '???'}
          </span>
        </span>
        <span style={{ color: '#555' }}>
          ITEMS: <span style={{ color: '#00ff41' }}>{gameState.inventory.length}</span>
        </span>
        <span style={{ color: '#555' }}>
          MOVES: <span style={{ color: '#ffb000' }}>{gameState.moves}</span>
        </span>
        <span style={{ color: '#555' }}>
          EXTRACTION: <span style={{ color: '#ff0040' }}>67%</span>
        </span>
        <span style={{ color: gameState.alive ? '#00ff41' : '#ff0040' }}>
          {gameState.alive ? (gameState.gameOver ? '◆ ESCAPED' : '♥ ALIVE') : '✝ SYNCED'}
        </span>
      </div>
    </div>
  );
}